import mainStyle from './port.module.scss';
import React, { useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";

import { MenuSelect } from "components/Select/menuSelect";
import { ReactComponent as ShowIcon } from "assets/images/icons/icn_input_show_hover.svg";
import { ReactComponent as HideIcon } from "assets/images/icons/icn_input_hide.svg";

const RadiusProfileSection = ({
  radiusProfile = null,
  onChangeRadiusProfile,
  radiusProfileOptions = [],
  radiusProfilesRaw = [],
  showEditGeneral,
}) => {
  const theme = useTheme();

  const activeServers = useMemo(() => {
    const servers = [];
    if (!radiusProfile) return servers;

    const profile = radiusProfilesRaw.find((p) => p.id === radiusProfile);
    if (profile) {
      if (profile.primary && profile.primary.authenticateServer) {
        servers.push({
          server: profile.primary.authenticateServer,
          port: profile.primary.port,
          secret: profile.primary.secret,
        });
      }
      if (profile.secondary && profile.secondary.authenticateServer) {
        servers.push({
          server: profile.secondary.authenticateServer,
          port: profile.secondary.port,
          secret: profile.secondary.secret,
        });
      }
    }
    return servers;
  }, [radiusProfile, radiusProfilesRaw]);

  const [showSecrets, setShowSecrets] = useState({});

  const toggleShowSecret = (idx) => {
    setShowSecrets((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  return (
    <Box sx={{ width: 'calc(100% - 40px)', boxSizing: 'border-box', ml: "20px" }}>
      <Box sx={{ ml: "123px", mb: 1.5 }}>
        <Typography
          sx={{
            fontSize: 12,
            color: theme.palette.primary.orange || "#e67e22",
            lineHeight: 1.5,
          }}
        >
          Each device supports independent Access Policy configuration. When Access Policy (802.1X) is enabled, a single global RADIUS server configuration is applied to all designated ports on that device. Any changes to the RADIUS settings are automatically synchronized across these ports in real time, eliminating the need to configure each port individually.
        </Typography>
      </Box>

      <Typography
        sx={{
          fontWeight: theme.typography.customFontWeight[1],
          fontSize: theme.typography.customFontSize[2],
          color: theme.palette.primary.greyPrimary,
          minWidth: "auto !important",
          mr: 0.5,
          mt: "20px",
        }}
      >
        RADIUS Profile
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", mt: "20px" }}>
        <Typography
          sx={{
            width: "120px",
            color: theme.palette.primary.greyPrimary,
            fontSize: theme.typography.customFontSize[2],
            fontWeight: theme.typography.customFontWeight[0],
            whiteSpace: "nowrap",
          }}
        >
          RADIUS Server
        </Typography>
        <MenuSelect
          width="376.25px"
          value={radiusProfile || ""}
          onChange={(opt) => onChangeRadiusProfile(opt)}
          options={radiusProfileOptions}
          placeholder="Select a RADIUS Profile"
          pinningOptions={[
            {
              value: "Edit/Create New Profile",
              label: "Edit/Create New Profile",
              showArrow: true,
            }
          ]}
          disabled={!showEditGeneral}
        />
      </Box>

      <Box sx={{ display: "flex", mt: '30px', px: 1 }}>
        <Typography
          sx={{
            fontWeight: theme.typography.customFontWeight[2],
            fontSize: theme.typography.customFontSize[1],
            color: theme.palette.primary.greySecondary,
            width: 65,
            pl: "22px",
          }}
        >
          #
        </Typography>
        <Typography
          sx={{
            fontWeight: theme.typography.customFontWeight[2],
            fontSize: theme.typography.customFontSize[1],
            color: theme.palette.primary.greySecondary,
            width: 170,
          }}
        >
          RADIUS Server
        </Typography>
        <Typography
          sx={{
            fontWeight: theme.typography.customFontWeight[2],
            fontSize: theme.typography.customFontSize[1],
            color: theme.palette.primary.greySecondary,
            width: 145
          }}
        >
          RADIUS Port
        </Typography>
        <Typography
          sx={{
            fontWeight: theme.typography.customFontWeight[2],
            fontSize: theme.typography.customFontSize[1],
            color: theme.palette.primary.greySecondary,
            width: 140
          }}
        >
          RADIUS Secret
        </Typography>
      </Box>

      <Box sx={{
        p: 2,
        border: `1px solid ${theme.palette.primary.borderGreyPrimary || theme.palette.primary.greyThread}`,
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        mb: "20px",
        backgroundColor: theme.palette.primary.white,
      }}>
        {activeServers.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Typography sx={{ color: theme.palette.primary.greyThird, fontSize: theme.typography.customFontSize[2] }}>
              No entry in the table
            </Typography>
          </Box>
        ) : (
          activeServers.map((serverItem, idx) => (
            <Box key={idx} sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                sx={{
                  color: theme.palette.primary.greyPrimary,
                  fontSize: theme.typography.customFontSize[2],
                  width: 55,
                  px: "15px"
                }}
              >
                {idx + 1}
              </Typography>
              <Typography
                sx={{
                  color: theme.palette.primary.greyPrimary,
                  fontSize: theme.typography.customFontSize[2],
                  width: 171
                }}
              >
                {serverItem.server}
              </Typography>
              <Typography
                sx={{
                  color: theme.palette.primary.greyPrimary,
                  fontSize: theme.typography.customFontSize[2],
                  width: 143
                }}
              >
                {serverItem.port}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: 140 }}>
                <Typography
                  sx={{
                    color: theme.palette.primary.greyPrimary,
                    fontSize: theme.typography.customFontSize[2],
                    textOverflow: showSecrets[idx] ? 'ellipsis' : 'clip',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    flexGrow: 1,
                    WebkitTextSecurity: showSecrets[idx] ? 'none' : 'disc',
                  }}
                >
                  {serverItem.secret || ""}
                </Typography>
                {serverItem.secret && (
                  <Box
                    onClick={() => toggleShowSecret(idx)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      width: 18,
                      height: 18,
                      flexShrink: 0,
                    }}
                  >
                    {showSecrets[idx] ? (
                      <ShowIcon sx={{ width: 18, height: 18 }} />
                    ) : (
                      <HideIcon sx={{ width: 18, height: 18 }} />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default React.memo(RadiusProfileSection);
